import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Save, History, Download, Plus, Trash2, FileText, FolderOpen, HelpCircle, MessageSquare, TrendingUp, Headphones, PlayCircle } from "lucide-react";
import FileUploader, { fileDownloadUrl } from "../components/FileUploader";
import CoachVoice from "../components/CoachVoice";
import ExerciseListEditor from "../components/ExerciseListEditor";
import GuidedWorkout from "../components/GuidedWorkout";

const CATEGORIES = [
  { id: "question",    label: "Question",    icon: HelpCircle,    color: "text-[color:var(--accent-2)]" },
  { id: "feedback",    label: "Feedback",    icon: MessageSquare, color: "text-[color:var(--ink-2)]" },
  { id: "progression", label: "Progression", icon: TrendingUp,    color: "text-[color:var(--accent)]" },
];
const CATEGORY_BY_ID = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

export default function WorkoutDetail() {
  const { programId } = useParams();
  const { user } = useAuth();
  const [program, setProgram] = useState(null);
  const [activeWorkout, setActiveWorkout] = useState(null);
  const [logsByExercise, setLogsByExercise] = useState({});
  const [draft, setDraft] = useState({});
  const [busyIdx, setBusyIdx] = useState(null);
  const [error, setError] = useState("");

  // updates / notes
  const [noteText, setNoteText] = useState("");
  const [noteFile, setNoteFile] = useState(null);
  const [noteCategory, setNoteCategory] = useState("question");
  const [noteBusy, setNoteBusy] = useState(false);
  const [filter, setFilter] = useState("all"); // all | question | feedback | progression

  // guided workout player
  const [guidedOpen, setGuidedOpen] = useState(false);

  const fetchProgram = () =>
    api.get(`/programs/${programId}`)
      .then((r) => {
        setProgram(r.data);
        if (!activeWorkout && r.data?.workouts?.length) setActiveWorkout(r.data.workouts[0]);
        // mark this folder as seen (best-effort, fire-and-forget)
        api.post(`/programs/${programId}/seen`).catch(() => {});
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));

  useEffect(() => {
    fetchProgram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  useEffect(() => {
    if (!program || !activeWorkout) return;
    api.get("/exercise-logs", { params: { program_id: program.id, workout_id: activeWorkout.id } })
      .then((r) => {
        const grouped = {};
        for (const l of r.data) {
          if (!grouped[l.exercise_index]) grouped[l.exercise_index] = [];
          grouped[l.exercise_index].push(l);
        }
        setLogsByExercise(grouped);
      })
      .catch(() => setLogsByExercise({}));
  }, [program, activeWorkout]);

  const updateDraft = (idx, field, value) => setDraft((d) => ({ ...d, [idx]: { ...(d[idx] || {}), [field]: value } }));

  const saveLog = async (idx) => {
    setBusyIdx(idx);
    try {
      const payload = {
        program_id: program.id, workout_id: activeWorkout.id, exercise_index: idx,
        weight: draft[idx]?.weight || null, reps_done: draft[idx]?.reps_done || null, notes: draft[idx]?.notes || null,
      };
      const { data } = await api.post("/exercise-logs", payload);
      setLogsByExercise((prev) => ({ ...prev, [idx]: [data, ...(prev[idx] || [])] }));
      setDraft((d) => ({ ...d, [idx]: {} }));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyIdx(null);
    }
  };

  const submitUpdate = async (e) => {
    e.preventDefault();
    if (!noteText.trim() && !noteFile) return;
    setNoteBusy(true);
    try {
      await api.post(`/programs/${program.id}/updates`, {
        category: noteCategory,
        content: noteText.trim() || null,
        file_id: noteFile?.id || null,
      });
      setNoteText("");
      setNoteFile(null);
      await fetchProgram();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setNoteBusy(false);
    }
  };

  const deleteUpdate = async (uid) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/programs/${program.id}/updates/${uid}`);
    fetchProgram();
  };

  // Admin-only: add a file to the directory
  const onAdminFileUploaded = async (f) => {
    try {
      await api.post(`/admin/programs/${program.id}/files`, { file_id: f.id });
      await fetchProgram();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  const removeFileEntry = async (entryId, name) => {
    if (!window.confirm(`Remove "${name}" from this folder?`)) return;
    await api.delete(`/admin/programs/${program.id}/files/${entryId}`);
    await fetchProgram();
  };

  if (error && !program) {
    return (
      <div className="min-h-screen pt-24 px-6 max-w-3xl mx-auto" data-testid="workout-error">
        <p className="text-[color:var(--accent-2)]">{error}</p>
        <Link to="/dashboard" className="btn-ghost mt-6"><ArrowLeft size={14} /> Back</Link>
      </div>
    );
  }
  if (!program) return <div className="min-h-screen pt-24 px-6 text-[color:var(--ink-3)] italic">Loading…</div>;

  const updates = (program.updates || []).slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const counts = {
    all: updates.length,
    question: updates.filter((u) => u.category === "question").length,
    feedback: updates.filter((u) => u.category === "feedback").length,
    progression: updates.filter((u) => u.category === "progression").length,
  };
  const visibleUpdates = filter === "all" ? updates : updates.filter((u) => u.category === filter);

  return (
    <div className="min-h-screen pt-24 pb-20 bg-[var(--bg)]" data-testid="workout-detail">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <Link to={user?.role === "admin" ? "/admin" : "/dashboard"} className="inline-flex items-center gap-1 text-sm text-[color:var(--ink-2)] hover:text-[color:var(--ink)]" data-testid="workout-back">
          <ArrowLeft size={14} strokeWidth={1.6} /> {user?.role === "admin" ? "back to studio" : "back to your space"}
        </Link>
        <p className="overline mt-6 mb-2 inline-flex items-center gap-2">
          <FolderOpen size={14} strokeWidth={1.6} className="text-[color:var(--accent)]" /> Program folder
        </p>
        <h1 className="font-serif font-light text-4xl text-[color:var(--ink)] tracking-tight">{program.title}</h1>
        {program.description && <p className="mt-3 text-[color:var(--ink-2)] max-w-2xl">{program.description}</p>}

        {/* Program directory: list of all files inside this folder */}
        <div className="mt-8" data-testid="program-directory">
          <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
            <p className="overline">Program directory ({(program.files || []).length} {(program.files || []).length === 1 ? "file" : "files"})</p>
            {user?.role === "admin" && (
              <FileUploader
                onUploaded={onAdminFileUploaded}
                accept=".pdf,application/pdf,image/*,video/*"
                label="+ Add file"
                testid="program-file-add-admin"
              />
            )}
          </div>

          {(program.files || []).length === 0 ? (
            <div className="card-soft p-6 border-dashed" data-testid="program-directory-empty">
              {user?.role === "admin" ? (
                <p className="text-sm text-[color:var(--ink-2)]">
                  Drop in PDFs, images or videos for your client. Click <span className="font-medium">+ Add file</span> above to upload — they'll appear here for your client to download.
                </p>
              ) : (
                <p className="text-sm text-[color:var(--ink-3)] italic">
                  Sopfie hasn't placed any files in this folder yet.
                </p>
              )}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {(program.files || []).map((f) => (
                <div key={f.id} className="card-soft p-4 flex items-center gap-3" data-testid={`program-directory-file-${f.id}`}>
                  <FileText size={20} strokeWidth={1.4} className="text-[color:var(--accent)] shrink-0" />
                  <a
                    href={fileDownloadUrl(f.file_id)}
                    target="_blank" rel="noreferrer"
                    className="flex-1 min-w-0 hover:text-[color:var(--accent)]"
                    title={f.file_name}
                  >
                    <p className="text-[color:var(--ink)] text-sm truncate">{f.label || f.file_name}</p>
                    {f.uploaded_at && (
                      <p className="text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] mt-0.5">
                        {new Date(f.uploaded_at).toLocaleDateString()}
                      </p>
                    )}
                  </a>
                  <a
                    href={fileDownloadUrl(f.file_id)}
                    target="_blank" rel="noreferrer"
                    className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"
                    title="Download"
                  >
                    <Download size={15} strokeWidth={1.5} />
                  </a>
                  {user?.role === "admin" && (
                    <button
                      onClick={() => removeFileEntry(f.id, f.label || f.file_name)}
                      className="text-[color:var(--ink-3)] hover:text-[color:var(--accent-2)]"
                      title="Remove"
                      data-testid={`program-directory-remove-${f.id}`}
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Voice-guided workout: editor for admin, launcher for client */}
        {(() => {
          const exercises = program.workouts?.[0]?.exercises || [];
          const hasExercises = exercises.length > 0;
          return (
            <div className="mt-8">
              {user?.role === "admin" && (
                <ExerciseListEditor program={program} onChanged={fetchProgram} />
              )}
              {user?.role !== "admin" && hasExercises && (
                <div className="card-soft p-6 md:p-8 bg-gradient-to-br from-[color:var(--bg)] to-[color:var(--bg-2)]" data-testid="guided-workout-launcher">
                  <div className="flex items-start gap-4">
                    <div className="h-14 w-14 rounded-full bg-[color:var(--accent)]/15 flex items-center justify-center shrink-0">
                      <Headphones size={22} strokeWidth={1.4} className="text-[color:var(--accent)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="overline">Voice-guided session</p>
                      <h3 className="font-serif text-2xl text-[color:var(--ink)] mt-1">Train with Sopfie in your ear</h3>
                      <p className="text-sm text-[color:var(--ink-2)] mt-2 leading-relaxed">
                        Press play and Sopfie will walk you through each of your {exercises.length} exercise{exercises.length !== 1 ? "s" : ""} — explaining how to set up, how to perform it, and what to feel for.
                        Say "done" when you finish and she'll move to the next one.
                      </p>
                      <button
                        onClick={() => setGuidedOpen(true)}
                        className="btn-primary mt-5"
                        data-testid="guided-workout-start"
                      >
                        <PlayCircle size={16} strokeWidth={1.6} /> Start the session
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Voice coaching — Sopfie in your pocket */}
        <div className="mt-8">
          <CoachVoice />
        </div>

        {/* Notes, questions & progressions */}
        <section className="mt-12">
          <h2 className="font-serif text-2xl text-[color:var(--ink)] flex items-center gap-2">
            <History size={18} strokeWidth={1.4} className="text-[color:var(--accent)]" />
            Notes, questions &amp; progressions
          </h2>
          <p className="text-sm text-[color:var(--ink-2)] mt-1">{user?.role === "admin"
            ? "Reply to your client here. Pick a sub-category, then post — your client will see it inside their folder."
            : "A shared timeline inside this folder. Pick a sub-category, then post — both you and Sopfie can write here."
          }</p>

          <form onSubmit={submitUpdate} className="card-soft p-5 mt-5" data-testid="program-update-form">
            <div className="flex flex-wrap gap-2 mb-3" data-testid="program-update-category-picker">
              {CATEGORIES.map((c) => {
                const active = noteCategory === c.id;
                const Icon = c.icon;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setNoteCategory(c.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs tracking-wide border transition-colors ${
                      active
                        ? "bg-[color:var(--ink)] text-[color:var(--bg)] border-[color:var(--ink)]"
                        : "bg-transparent text-[color:var(--ink-2)] border-[color:var(--line)] hover:border-[color:var(--ink)]"
                    }`}
                    data-testid={`program-update-category-${c.id}`}
                  >
                    <Icon size={13} strokeWidth={1.6} /> {c.label}
                  </button>
                );
              })}
            </div>
            <textarea
              className="input-warm" rows={3}
              placeholder={
                noteCategory === "question"   ? "Ask Sopfie a question about this program…" :
                noteCategory === "feedback"   ? "How is the program feeling? What's working, what's not?" :
                                                "Share progress — a new lift, a new rep, how your body is responding."
              }
              value={noteText} onChange={(e) => setNoteText(e.target.value)}
              data-testid="program-update-content"
            />
            <div className="flex flex-wrap items-center gap-3 mt-3">
              {noteFile ? (
                <div className="flex items-center gap-2 text-sm text-[color:var(--ink)]">📎 {noteFile.original_filename}<button type="button" className="text-xs underline text-[color:var(--accent-2)]" onClick={() => setNoteFile(null)}>remove</button></div>
              ) : (
                <FileUploader onUploaded={setNoteFile} accept=".pdf,application/pdf,image/*,video/*" label="Attach file" testid="program-update-upload" />
              )}
              <div className="flex-1" />
              <button disabled={noteBusy || (!noteText.trim() && !noteFile)} className="btn-primary !py-2 !px-4 !text-sm" data-testid="program-update-submit">
                <Plus size={14} strokeWidth={1.6} /> {noteBusy ? "Posting…" : "Post"}
              </button>
            </div>
          </form>

          {/* Filter tabs */}
          <div className="mt-7 flex flex-wrap gap-2 border-b border-[color:var(--line-soft)] pb-1" data-testid="program-update-filter-tabs">
            {[
              { id: "all", label: "All" },
              ...CATEGORIES.map((c) => ({ id: c.id, label: c.label + "s" })),
            ].map((t) => {
              const active = filter === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setFilter(t.id)}
                  className={`px-4 py-2 text-sm transition-colors -mb-px border-b-2 ${
                    active
                      ? "border-[color:var(--accent)] text-[color:var(--ink)]"
                      : "border-transparent text-[color:var(--ink-3)] hover:text-[color:var(--ink-2)]"
                  }`}
                  data-testid={`program-update-filter-${t.id}`}
                >
                  {t.label} <span className="ml-1 text-xs text-[color:var(--ink-3)]">({counts[t.id] || 0})</span>
                </button>
              );
            })}
          </div>

          <div className="mt-5 space-y-3" data-testid="program-updates-list">
            {visibleUpdates.length === 0 && (
              <p className="text-[color:var(--ink-3)] italic font-serif" data-testid="program-updates-empty">
                {filter === "all" ? "No notes yet — start the conversation." : `No ${filter}s yet.`}
              </p>
            )}
            {visibleUpdates.map((u) => {
              const cat = u.category ? CATEGORY_BY_ID[u.category] : null;
              const CatIcon = cat?.icon;
              return (
                <article key={u.id} className="card-soft p-5" data-testid={`program-update-${u.id}`}>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 flex-wrap">
                      {cat && (
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-medium ${cat.color}`} data-testid={`program-update-${u.id}-category`}>
                          <CatIcon size={11} strokeWidth={1.8} /> {cat.label}
                        </span>
                      )}
                      <p className="text-sm">
                        <span className="font-medium text-[color:var(--ink)]">{u.author_name}</span>
                        <span className="ml-2 text-xs uppercase tracking-widest text-[color:var(--ink-3)]">{u.author_role === "admin" ? "Sopfie" : "Client"}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-[color:var(--ink-3)]">{new Date(u.created_at).toLocaleString()}</span>
                      {(user?.role === "admin" || u.author_id === user?.id) && (
                        <button onClick={() => deleteUpdate(u.id)} className="text-[color:var(--ink-3)] hover:text-[color:var(--accent-2)]" data-testid={`program-update-delete-${u.id}`}><Trash2 size={13} /></button>
                      )}
                    </div>
                  </div>
                  {u.content && <p className="mt-3 text-[color:var(--ink)] leading-relaxed whitespace-pre-wrap">{u.content}</p>}
                  {u.file_id && (
                    <a href={fileDownloadUrl(u.file_id)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm text-[color:var(--accent-2)] hover:text-[color:var(--accent)]" data-testid={`program-update-file-${u.id}`}>
                      <FileText size={14} strokeWidth={1.5} /> {u.file_name || "attached file"} <Download size={12} />
                    </a>
                  )}
                </article>
              );
            })}
          </div>
        </section>

        {/* Inline workouts (legacy / optional) */}
        {(program.workouts || []).length > 0 && activeWorkout && (
          <section className="mt-14">
            <h2 className="font-serif text-2xl text-[color:var(--ink)]">Workouts</h2>
            {(program.workouts || []).length > 1 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="workout-tabs">
                {program.workouts.map((w) => (
                  <button
                    key={w.id} onClick={() => setActiveWorkout(w)}
                    className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                      activeWorkout?.id === w.id
                        ? "bg-[color:var(--ink)] text-[color:var(--bg)] border-[color:var(--ink)]"
                        : "bg-transparent text-[color:var(--ink-2)] border-[color:var(--line)] hover:border-[color:var(--ink)]"
                    }`}
                    data-testid={`workout-tab-${w.id}`}
                  >
                    {w.day_label || w.title}
                  </button>
                ))}
              </div>
            )}

            <h3 className="mt-6 font-serif text-2xl text-[color:var(--ink)]">{activeWorkout.title}</h3>
            {activeWorkout.description && <p className="text-[color:var(--ink-2)] mt-2">{activeWorkout.description}</p>}

            <div className="mt-6 space-y-5">
              {(activeWorkout.exercises || []).map((ex, idx) => {
                const logs = logsByExercise[idx] || [];
                const d = draft[idx] || {};
                return (
                  <div key={idx} className="card-soft p-6" data-testid={`exercise-${idx}`}>
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 justify-between">
                      <div>
                        <p className="overline">Exercise {idx + 1}</p>
                        <h4 className="font-serif text-2xl text-[color:var(--ink)] mt-1">{ex.name}</h4>
                      </div>
                      <p className="text-sm text-[color:var(--ink-2)]">
                        {ex.sets ? `${ex.sets} sets` : ""}
                        {ex.reps ? ` · ${ex.reps} reps` : ""}
                        {ex.rest ? ` · rest ${ex.rest}` : ""}
                      </p>
                    </div>
                    {ex.notes && <p className="mt-2 text-sm italic text-[color:var(--ink-2)]">{ex.notes}</p>}

                    <div className="mt-5 grid sm:grid-cols-3 gap-3">
                      <input className="input-warm" placeholder="Weight" value={d.weight || ""} onChange={(e) => updateDraft(idx, "weight", e.target.value)} data-testid={`log-weight-${idx}`} />
                      <input className="input-warm" placeholder="Reps done" value={d.reps_done || ""} onChange={(e) => updateDraft(idx, "reps_done", e.target.value)} data-testid={`log-reps-${idx}`} />
                      <button onClick={() => saveLog(idx)} disabled={busyIdx === idx} className="btn-primary justify-center" data-testid={`log-save-${idx}`}>
                        <Save size={14} strokeWidth={1.6} /> {busyIdx === idx ? "Saving" : "Save"}
                      </button>
                    </div>
                    <textarea className="input-warm mt-3" rows={2} placeholder="A note to yourself…" value={d.notes || ""} onChange={(e) => updateDraft(idx, "notes", e.target.value)} data-testid={`log-notes-${idx}`} />

                    {logs.length > 0 && (
                      <div className="mt-6 border-t border-[color:var(--line-soft)] pt-4">
                        <p className="overline flex items-center gap-1.5 mb-3"><History size={12} strokeWidth={1.6} /> Your history</p>
                        <ul className="space-y-2 max-h-48 overflow-auto pr-1">
                          {logs.map((l) => (
                            <li key={l.id} className="text-sm text-[color:var(--ink-2)] flex flex-wrap gap-x-3" data-testid={`log-entry-${l.id}`}>
                              <span className="text-[color:var(--ink-3)]">{new Date(l.created_at).toLocaleDateString()}</span>
                              {l.weight && <span><b className="text-[color:var(--ink)]">{l.weight}</b> · weight</span>}
                              {l.reps_done && <span><b className="text-[color:var(--ink)]">{l.reps_done}</b> · reps</span>}
                              {l.notes && <span className="italic">"{l.notes}"</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* Full-screen guided workout (portal) */}
      {guidedOpen && (
        <GuidedWorkout program={program} onClose={() => setGuidedOpen(false)} />
      )}
    </div>
  );
}
